import { cn } from 'lib/utils'

import { asArray, asText } from '../payload'

/**
 * Renderers for the generic elements (MYD-495). Each is payload in, JSX out:
 * no hooks, no fetching, no store. The payload is exactly what the element's
 * `select` returned on the server (backend/presentations/providers/
 * genericElements.js), so the shapes below mirror that file.
 *
 * Sizes are logical pixels on the 1280x720 stage.
 *
 * Payloads come off the network, so each renderer tolerates a missing or
 * malformed field rather than throwing inside a presentation.
 */

type Align = 'left' | 'center'
const alignClass = (align?: Align) => (align === 'center' ? 'text-center' : 'text-left')


// ---------------------------------------------------------------------------

const HEADING_SIZES = { large: 'text-[56px]', medium: 'text-[40px]', small: 'text-[28px]' } as const

export function HeadingBlock({ payload }: { payload: any }) {
	const size = HEADING_SIZES[payload?.size as keyof typeof HEADING_SIZES] ?? HEADING_SIZES.large
	return (
		<h2 className={cn('font-bold leading-tight text-gray-900', size, alignClass(payload?.align))}>
			{asText(payload?.text)}
		</h2>
	)
}

// ---------------------------------------------------------------------------

interface Run {
	text: string
	bold?: boolean
	italic?: boolean
}

/** One run of text. Rendered as elements, never as HTML. */
const RunText = ({ run }: { run: Run }) => {
	const text = asText(run?.text)
	if (run?.bold && run?.italic) return <strong><em>{text}</em></strong>
	if (run?.bold) return <strong className="font-semibold">{text}</strong>
	if (run?.italic) return <em>{text}</em>
	return <>{text}</>
}

const Runs = ({ runs }: { runs: unknown }) => (
	<>
		{asArray<Run>(runs).map((run, index) => (
			<RunText key={index} run={run} />
		))}
	</>
)

export function TextBlock({ payload }: { payload: any }) {
	return (
		<div className={cn('space-y-4 text-[24px] leading-relaxed text-gray-700', alignClass(payload?.align))}>
			{asArray<any>(payload?.blocks).map((block, index) =>
				block?.type === 'list' ? (
					<ul key={index} className="list-disc space-y-2 pl-8">
						{asArray<unknown>(block.items).map((runs, item) => (
							<li key={item}>
								<Runs runs={runs} />
							</li>
						))}
					</ul>
				) : (
					<p key={index}>
						<Runs runs={block?.runs} />
					</p>
				),
			)}
		</div>
	)
}

// ---------------------------------------------------------------------------

export function BulletsBlock({ payload }: { payload: any }) {
	return (
		<ul className="list-disc space-y-3 pl-8 text-[26px] leading-snug text-gray-700">
			{asArray<unknown>(payload?.items).map((item, index) => (
				<li key={index}>{asText(item)}</li>
			))}
		</ul>
	)
}

// ---------------------------------------------------------------------------

/** One column per figure, up to the element's cap of four. */
const KPI_COLUMNS = ['grid-cols-1', 'grid-cols-1', 'grid-cols-2', 'grid-cols-3', 'grid-cols-4']

/** One figure's tile, shared by the single figure and the row so the two always match. */
function FigureTile({ figure, className }: { figure: any; className?: string }) {
	const caption = asText(figure?.caption)
	return (
		<div className={cn('rounded-xl bg-gray-50 px-6 py-5', className)}>
			<div className="text-[18px] font-medium uppercase tracking-wide text-gray-500">{asText(figure?.label)}</div>
			<div className="mt-1 text-[44px] font-bold leading-none text-primary">{asText(figure?.value)}</div>
			{caption && <div className="mt-2 text-[16px] text-gray-500">{caption}</div>}
		</div>
	)
}

/**
 * One figure filling its box (MYD-513), for the Key figures layout's boxes.
 * Centred vertically, so four boxes of one row line up whatever their captions.
 */
export function KpiBlock({ payload }: { payload: any }) {
	return <FigureTile figure={payload} className="flex h-full flex-col justify-center" />
}

export function KpisBlock({ payload }: { payload: any }) {
	const items = asArray<any>(payload?.items).slice(0, 4)
	return (
		<div className={cn('grid gap-6', KPI_COLUMNS[items.length])}>
			{items.map((item, index) => (
				<FigureTile key={index} figure={item} />
			))}
		</div>
	)
}

// ---------------------------------------------------------------------------

export function ImageBlock({ payload }: { payload: any }) {
	const url = asText(payload?.url)
	const caption = asText(payload?.caption)
	return (
		<figure className="flex h-full min-h-0 flex-col gap-3">
			<div className="min-h-0 flex-1 overflow-hidden rounded-lg bg-gray-100">
				{url && (
					<img
						src={url}
						alt={asText(payload?.alt)}
						className={cn('h-full w-full', payload?.fit === 'contain' ? 'object-contain' : 'object-cover')}
						draggable={false}
					/>
				)}
			</div>
			{caption && <figcaption className="text-[18px] text-gray-500">{caption}</figcaption>}
		</figure>
	)
}

// ---------------------------------------------------------------------------

export function DividerBlock({ payload }: { payload: any }) {
	return payload?.style === 'space' ? <div className="h-10" aria-hidden /> : <hr className="my-4 border-t-2 border-gray-200" />
}
